/**
 * THE PROPERTY WORDS (Independent Stays plan IS2, IS-COMMON §2 "Wording").
 *
 * In an organisation without Lobbero Desk a building and its units are a
 * PROPERTY and its APARTMENTS: "Add a property", "Your properties". The word
 * "building" appears only as "address". With Desk every screen says what it
 * always said. ONE map, read through `w(key, desk)`, never a per-screen
 * ternary — so a Stays-only screen cannot drift into Desk words by accident
 * (the IS1 wording sweep, e2e-audit/lib/desk-words.ts, checks the result).
 *
 * `desk` is the organisation's Desk product (utils/products.ts deskInPlan,
 * components/hooks/use-desk-in-plan.ts). Absent products read as Desk.
 *
 * IS9 adds hotel words (rooms, room types) next to these; keep the keys open.
 */

export type WordKey =
  | 'building'
  | 'buildings'
  | 'Building'
  | 'Buildings'
  | 'allBuildings'
  | 'otherBuildings'
  | 'unit'
  | 'units'
  | 'Unit'
  | 'Units'
  | 'unitNumbered'
  | 'noBuildings'
  | 'buildingOff'
  | 'buildingOffAction'
  | 'noListingsYet'
  | 'noListingsYetHow'
  | 'listingsLead'
  | 'chooseBuilding'
  | 'buildingTime'
  | 'timeOfPlace'
  | 'buildingDates'
  | 'buildingCode'
  | 'buildingAddress'
  | 'promoteAgain'
  | 'promoteAgainForStays'
  | 'openUnit'
  | 'notInScope'
  | 'setupFirstStepTitle'
  | 'setupFirstStepWhy'
  | 'addLocationFirst';

type Pair = { desk: string; stays: string };

// A unit is an "apartment" only where the property has several of them; in
// general copy a Stays-only unit is a "place" (a whole house is one place).
const WORDS: Record<WordKey, Pair> = {
  building: { desk: 'building', stays: 'property' },
  buildings: { desk: 'buildings', stays: 'properties' },
  Building: { desk: 'Building', stays: 'Property' },
  Buildings: { desk: 'Buildings', stays: 'Properties' },
  allBuildings: { desk: 'All buildings', stays: 'All properties' },
  otherBuildings: { desk: 'Other buildings', stays: 'Other properties' },
  unit: { desk: 'unit', stays: 'place' },
  units: { desk: 'units', stays: 'places' },
  Unit: { desk: 'Unit', stays: 'Place' },
  Units: { desk: 'Units', stays: 'Places' },
  /** "Unit 4B" / "Apartment 4B" — {n} is the number (several apartments only). */
  unitNumbered: { desk: 'Unit {n}', stays: 'Apartment {n}' },
  noBuildings: {
    desk: 'You have no buildings yet. Add one in Buildings first.',
    stays: 'You have no properties yet. Add your first one here.',
  },
  buildingOff: {
    desk: "Switch Stays on in the building's settings before you promote a unit here.",
    stays: 'This property is not open to Stays yet. Open it from Properties.',
  },
  buildingOffAction: { desk: 'Open building settings', stays: 'Open Properties' },
  noListingsYet: { desk: 'No units on Stays yet', stays: 'No properties on Stays yet' },
  noListingsYetHow: {
    desk: 'Promote a unit from its page in Units to list it for short stays.',
    stays: 'Add your first property and the setup guide walks you through the rest.',
  },
  listingsLead: {
    desk: 'Units you list for short-term guests: what guests see them called, whether they are live, and who hosts them.',
    stays: 'Places you let to short-term guests: what guests see them called, whether they are live, and who hosts them.',
  },
  chooseBuilding: {
    desk: 'Choose a building to see its upcoming stays.',
    stays: 'Choose a property to see its upcoming stays.',
  },
  /** "Building time" / "Local time at the property". */
  buildingTime: { desk: 'Building time', stays: 'Local time at the property' },
  /** "15:00 building time" / "15:00 local time" after a clock time. */
  timeOfPlace: { desk: 'building time', stays: 'local time' },
  buildingDates: { desk: 'Building dates', stays: 'Dates at the property' },
  buildingCode: { desk: 'Building code', stays: 'Street door code' },
  /** "Uses the building address: …" */
  buildingAddress: { desk: 'Uses the building address', stays: 'Uses the property address' },
  promoteAgain: {
    desk: 'Promote the unit again to list it.',
    stays: 'Set it up again from the setup guide to list it.',
  },
  promoteAgainForStays: {
    desk: 'Promote the unit again to add stays.',
    stays: 'Set it up again from the setup guide to add stays.',
  },
  openUnit: { desk: 'Open unit', stays: 'Open Properties' },
  notInScope: {
    desk: 'It may be in a building you do not cover. Go back to Stays to see your listings.',
    stays: 'It may be at a property you do not cover. Go back to Stays to see your listings.',
  },
  setupFirstStepTitle: { desk: 'Which unit', stays: 'Your property' },
  setupFirstStepWhy: {
    desk: 'Pick the home guests will stay in. Its building must have Stays switched on.',
    stays: 'Tell us about the place guests will stay in: a whole place, an apartment, or several apartments at one address.',
  },
  addLocationFirst: {
    desk: "Add the building's location first (Buildings, Edit building).",
    stays: "Add the property's address first (Properties, Edit address).",
  },
};

/** The word for `key` in this organisation. `vars` fill {n}, {b}, … */
export const w = (key: WordKey, desk: boolean, vars: Record<string, string | number> = {}): string => {
  const pair = WORDS[key];
  const text = desk ? pair.desk : pair.stays;

  return text.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
};

/**
 * A fixed label from a Desk-era label map ("Unit", "Building", "Building code"
 * in the template chips and the door-access fields) in this organisation's
 * words. Labels not in the map are returned as they are.
 */
const LABELS: Record<string, WordKey> = {
  Unit: 'Unit',
  Building: 'Building',
  'Building code': 'buildingCode',
};

export const relabel = (label: string, desk: boolean): string => (LABELS[label] ? w(LABELS[label], desk) : label);

/** The property kinds a Stays-only host chooses from (step 1 of the setup guide). */
export const LAYOUT_WORDS: Record<'whole' | 'apartment' | 'multi' | 'hotel', { title: string; hint: string; short: string }> = {
  whole: {
    title: 'A whole place',
    hint: 'A house, a cottage or a villa guests have to themselves.',
    short: 'Whole place',
  },
  apartment: {
    title: 'An apartment',
    hint: 'One apartment at an address shared with other homes.',
    short: 'Apartment',
  },
  multi: {
    title: 'Several apartments at one address',
    hint: 'Two or more apartments you let at the same address.',
    short: 'Several apartments',
  },
  // IS9: a hotel — rooms grouped in room types, a reception.
  hotel: {
    title: 'A hotel',
    hint: 'Rooms grouped by type, like Double or Suite, with a reception.',
    short: 'Hotel',
  },
};

/**
 * IS9: a hotel's words. A hotel unit is a ROOM, groups of rooms are ROOM
 * TYPES, and the concierge role is shown as RECEPTION (R-IS9 Q2).
 */
export const HOTEL_WORDS = {
  room: 'room',
  rooms: 'rooms',
  Room: 'Room',
  roomType: 'room type',
  roomTypes: 'room types',
  RoomType: 'Room type',
  reception: 'Reception',
  roomNumbered: 'Room {n}',
} as const;

/** Exported for the test: every key has both words, and no Stays word is a Desk word. */
export const WORD_KEYS = Object.keys(WORDS) as WordKey[];
